import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "clarityscribe_tour_completed_v1";

/**
 * Lightweight 3-step tour. Renders a full-screen dim overlay with a cutout
 * around the current target element and a floating tooltip.
 *
 * Props:
 *  - open: boolean
 *  - steps: [{selector, title, body, placement?}]
 *  - onClose: () => void
 */
export function Tour({ open, steps, onClose }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const targetRef = useRef(null);

  const currentStep = steps[index];

  const measure = () => {
    if (!currentStep) return;
    const el = document.querySelector(currentStep.selector);
    targetRef.current = el;
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    // Scroll target into view softly
    const el = targetRef.current;
    if (el && "scrollIntoView" in el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // Remeasure after scroll settles
    const t = setTimeout(measure, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);

  if (!open) return null;
  if (!currentStep) return null;

  const next = () => {
    if (index >= steps.length - 1) {
      onClose?.();
      return;
    }
    setIndex((i) => i + 1);
  };
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  const pad = 8;
  const cutout = rect
    ? {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null;

  // Tooltip placement: below by default, above if not enough room
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const tooltipWidth = Math.min(340, vw - 24);
  let tooltipTop = 24;
  let tooltipLeft = (vw - tooltipWidth) / 2;
  if (cutout) {
    const spaceBelow = vh - (cutout.top + cutout.height);
    const preferBelow = spaceBelow > 220;
    tooltipTop = preferBelow
      ? Math.min(vh - 240, cutout.top + cutout.height + 12)
      : Math.max(12, cutout.top - 200);
    tooltipLeft = Math.min(
      vw - tooltipWidth - 12,
      Math.max(12, cutout.left + cutout.width / 2 - tooltipWidth / 2)
    );
  }

  return createPortal(
    <div
      data-testid="onboarding-tour"
      className="pointer-events-none fixed inset-0 z-[1000]"
    >
      {/* Dim overlay with SVG cutout */}
      <svg
        className="pointer-events-auto absolute inset-0 h-full w-full"
        onClick={onClose}
      >
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {cutout && (
              <rect
                x={cutout.left}
                y={cutout.top}
                width={cutout.width}
                height={cutout.height}
                rx="16"
                ry="16"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(15, 17, 21, 0.55)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Highlight ring on target */}
      {cutout && (
        <div
          data-testid="onboarding-tour-highlight"
          className="pointer-events-none absolute rounded-2xl ring-2 ring-amber-300 ring-offset-2 ring-offset-transparent"
          style={{
            top: cutout.top,
            left: cutout.left,
            width: cutout.width,
            height: cutout.height,
            boxShadow:
              "0 0 0 6px rgba(252, 211, 77, 0.18), 0 20px 40px -12px rgba(0,0,0,0.35)",
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        data-testid="onboarding-tour-tooltip"
        className="pointer-events-auto absolute rounded-2xl border border-[#E6E2D8] bg-white p-5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.35)]"
        style={{ top: tooltipTop, left: tooltipLeft, width: tooltipWidth }}
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-white">
              <Sparkles className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Step {index + 1} of {steps.length}
            </span>
          </div>
          <button
            data-testid="onboarding-tour-close"
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-heading text-lg font-bold text-[#1A1D20]">
          {currentStep.title}
        </h3>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          {currentStep.body}
        </p>
        <div className="mt-4 flex items-center justify-between">
          <button
            data-testid="onboarding-tour-skip"
            onClick={onClose}
            className="text-xs font-medium text-slate-500 hover:text-slate-800 hover:underline"
          >
            Skip tour
          </button>
          <div className="flex gap-2">
            {index > 0 && (
              <Button
                variant="outline"
                data-testid="onboarding-tour-back"
                onClick={prev}
                className="h-9 gap-1 rounded-full border-[#E6E2D8] px-3 text-xs font-semibold"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            )}
            <Button
              data-testid="onboarding-tour-next"
              onClick={next}
              className="h-9 gap-1 rounded-full bg-[#1A1D20] px-3 text-xs font-semibold text-[#FAF9F5] hover:bg-[#2A2E3D]"
            >
              {index >= steps.length - 1 ? "Got it" : "Next"}
              {index < steps.length - 1 && (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export const HOME_TOUR_STEPS = [
  {
    selector: "[data-testid='actions-grid']",
    title: "Pick what you want the AI to do",
    body: "Fix grammar, simplify, make it professional, shorten, rewrite, or brainstorm ideas — all in one click.",
  },
  {
    selector: "[data-testid='generate-ai-button']",
    title: "Hit Generate — that's it",
    body: "Paste any text into the box, choose an action, and press Generate. The revised version appears on the right, ready to copy.",
  },
  {
    selector: "[data-testid='usage-pill']",
    title: "You have 5 free rewrites a day",
    body: "Need unlimited? Click the pill or your avatar to upgrade to Pro — $9/month or $79/year.",
  },
];

export function hasSeenTour() {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}
export function markTourSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  } catch {
    /* ignore */
  }
}
export function clearTourSeen() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
